import { create } from 'zustand';
import { Vehicle } from '../types';
import { 
  getVehicles, 
  getVehicleById, 
  createVehicle, 
  updateVehicle, 
  deleteVehicle 
} from '../api/supabase';
import { vehicleHybridStore, useNetworkStatus } from '../services/offline';
import { offlineApi, OFFLINE_MODE } from '../api/offline-data';
import { syncVehicleStatus } from '../utils/vehicleStatusSync';
import { runVehicleAudit } from '../utils/vehicleAssignmentAuditor';

interface VehicleState {
  vehicles: Vehicle[];
  selectedVehicle: Vehicle | null;
  loading: boolean;
  error: string | null;
  
  fetchVehicles: () => Promise<void>;
  fetchVehicleById: (id: string) => Promise<void>;
  addVehicle: (vehicle: Omit<Vehicle, 'id' | 'created_at'>) => Promise<void>;
  editVehicle: (id: string, vehicle: Partial<Vehicle>) => Promise<void>;
  removeVehicle: (id: string) => Promise<void>;
  setSelectedVehicle: (vehicle: Vehicle | null) => void;
  assignVehicle: (vehicleId: string, driverId: string) => Promise<void>;
  unassignVehicle: (vehicleId: string) => Promise<void>;
  unassignVehicleWithDelivery: (vehicleId: string, navigation: any) => void;
}

export const useVehicleStore = create<VehicleState>((set, get) => ({
  vehicles: [],
  selectedVehicle: null,
  loading: false,
  error: null,
  
  fetchVehicles: async () => {
    set({ loading: true, error: null });
    try {
      console.log('🚗 VehicleStore: Fetching vehicles (hybrid)...');
      
      // Get vehicles from hybrid storage (localStorage cache + SQLite backup)
      const vehicles = await vehicleHybridStore.getAll();
      console.log('🚗 VehicleStore: Received vehicles from hybrid storage:', vehicles.length);
      
      // Set initial vehicles (even if empty) to remove loading state
      set({ vehicles, loading: false });
      
      // Try to refresh from server if online (background refresh)
      // Skip if we've recently updated assignments to prevent state conflicts
      const lastAssignmentUpdate = localStorage.getItem('lastVehicleAssignmentUpdate');
      const timeSinceLastUpdate = lastAssignmentUpdate ? Date.now() - parseInt(lastAssignmentUpdate) : Infinity;
      
      if (timeSinceLastUpdate < 5000) {
        console.log('⏳ Skipping background refresh - recent assignment update detected');
        return;
      }
      
      setTimeout(async () => {
        try {
          // Only refresh if no recent assignment operations
          const currentLastUpdate = localStorage.getItem('lastVehicleAssignmentUpdate');
          const currentTimeSince = currentLastUpdate ? Date.now() - parseInt(currentLastUpdate) : Infinity;
          
          if (currentTimeSince < 5000) {
            console.log('⏳ Cancelling background refresh - assignment in progress');
            return;
          }
          
          await vehicleHybridStore.refreshFromServer();
          
          // Get updated vehicles after refresh
          const updatedVehicles = await vehicleHybridStore.getAll();
          console.log('🚗 VehicleStore: Updated vehicles after server refresh:', updatedVehicles.length);
          
          // Update store with fresh data
          set({ vehicles: updatedVehicles });
          
          // Run audit check after refresh
          const auditResult = await runVehicleAudit();
          if (auditResult.fixedCount > 0) {
            console.log(`🔧 Background sync: Fixed ${auditResult.fixedCount} vehicle inconsistencies`);
            // Get vehicles again after audit fixes
            const finalVehicles = await vehicleHybridStore.getAll();
            set({ vehicles: finalVehicles });
          }
        } catch (syncError) {
          console.warn('⚠️ Background vehicle refresh failed:', syncError);
        }
      }, 1000);
      
    } catch (error: any) {
      console.error('🚗 VehicleStore: Error fetching vehicles:', error);
      set({ loading: false, error: error.message });
    }
  },
  
  fetchVehicleById: async (id) => {
    set({ loading: true, error: null });
    try {
      const vehicle = await vehicleHybridStore.getById(id);
      set({ selectedVehicle: vehicle, loading: false });
    } catch (error: any) {
      set({ loading: false, error: error.message });
    }
  },
  
  addVehicle: async (vehicle) => {
    set({ loading: true, error: null });
    try {
      // Create vehicle using hybrid storage (offline-first)
      const vehicleData = {
        license_plate: vehicle.licensePlate,
        make_model: vehicle.makeModel,
        year: typeof vehicle.year === 'number' ? vehicle.year : parseInt(vehicle.year) || 0,
        vin: vehicle.vinNumber || '', // Use 'vin' column
        image_url: vehicle.image_url,
        car_status: vehicle.carStatus,
        current_driver_id: vehicle.currentDriverId,
        daily_rental_rate: vehicle.dailyRent, // Use 'daily_rental_rate' column
        notes: vehicle.notes,
      };

      const newVehicle = await vehicleHybridStore.create(vehicleData);
      console.log('✅ Vehicle created (hybrid):', newVehicle);
      
      // Log activity after successful creation
      try {
        const { logActivity } = require('../api/activityLog');
        const vehicleInfo = `${newVehicle.license_plate} (${newVehicle.make_model} ${newVehicle.year || ''})`;
        
        await logActivity({
          entity_type: 'vehicle',
          entity_id: newVehicle.id,
          action_type: 'create',
          changes: {
            vehicle: { before: null, after: vehicleInfo },
            status: { before: null, after: newVehicle.car_status },
            vin: { before: null, after: newVehicle.vin },
            daily_rent: { before: null, after: newVehicle.daily_rental_rate },
          },
          description: `Created new vehicle: ${vehicleInfo}`,
        });
        console.log('✅ Vehicle creation logged to activity log');
      } catch (logError) {
        console.error('⚠️ Failed to log vehicle creation:', logError);
        // Don't fail the creation if logging fails
      }
      
      // Convert to app format if needed
      const formattedVehicle: Vehicle = {
        id: newVehicle.id,
        licensePlate: newVehicle.license_plate,
        makeModel: newVehicle.make_model,
        year: newVehicle.year,
        vinNumber: newVehicle.vin, // Use 'vin' column
        image_url: newVehicle.image_url,
        carStatus: newVehicle.car_status as any,
        currentDriverId: newVehicle.current_driver_id,
        dailyRent: newVehicle.daily_rental_rate, // Use 'daily_rental_rate' column
        notes: newVehicle.notes,
        created_at: newVehicle.created_at,
      };
      
      set({ 
        vehicles: [...get().vehicles, formattedVehicle],
        selectedVehicle: formattedVehicle,
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error creating vehicle (hybrid):', error);
      set({ loading: false, error: error.message });
    }
  },
  
  editVehicle: async (id, vehicle) => {
    set({ loading: true, error: null });
    try {
      // Get current vehicle data for activity logging
      const currentVehicle = get().vehicles.find(v => v.id === id);
      
      // Convert from app schema to database schema
      const dbVehicle: any = {};
      
      if (vehicle.licensePlate !== undefined) {
        dbVehicle.license_plate = vehicle.licensePlate;
        console.log('🔍 Setting license_plate to:', vehicle.licensePlate);
      }
      if (vehicle.makeModel) dbVehicle.make_model = vehicle.makeModel;
      if (vehicle.year) dbVehicle.year = vehicle.year;
      if (vehicle.vinNumber) dbVehicle.vin = vehicle.vinNumber;
      if (vehicle.image_url) dbVehicle.image_url = vehicle.image_url;
      if (vehicle.carStatus) dbVehicle.car_status = vehicle.carStatus;
      if (vehicle.currentDriverId) dbVehicle.current_driver_id = vehicle.currentDriverId;
      if (vehicle.dailyRent) dbVehicle.daily_rental_rate = vehicle.dailyRent;
      if (vehicle.dailyRentalRate) dbVehicle.daily_rental_rate = vehicle.dailyRentalRate;
      if (vehicle.monthlyRentalRate) dbVehicle.monthly_rental_rate = vehicle.monthlyRentalRate;
      if (vehicle.insuranceExpiry) dbVehicle.insurance_expiry = vehicle.insuranceExpiry;
      if (vehicle.registrationExpiry) dbVehicle.registration_expiry = vehicle.registrationExpiry;
      if (vehicle.notes) dbVehicle.notes = vehicle.notes;
      
      console.log('📤 Updating vehicle directly in database:', { id, dbVehicle });
      
      // BYPASS HYBRID STORAGE - Go directly to Supabase
      const { supabase } = require('../api/supabase');
      const { data: updatedData, error: updateError } = await supabase
        .from('vehicles')
        .update(dbVehicle)
        .eq('id', id)
        .select()
        .single();
      
      if (updateError) {
        throw new Error(`Database update failed: ${updateError.message}`);
      }
      
      console.log('✅ Vehicle updated directly in database:', updatedData);
      
      // Convert response back to app schema
      const updatedVehicle = updatedData;
      
      // Log activity with before/after values
      try {
        const { logActivity } = require('../api/activityLog');
        
        const changes: Record<string, { before: any; after: any }> = {};
        const changedFields: string[] = [];
        
        if (currentVehicle) {
          if (vehicle.licensePlate && currentVehicle.licensePlate !== vehicle.licensePlate) {
            changes['License Plate'] = { before: currentVehicle.licensePlate, after: vehicle.licensePlate };
            changedFields.push('license plate');
          }
          if (vehicle.makeModel && currentVehicle.makeModel !== vehicle.makeModel) {
            changes['Make/Model'] = { before: currentVehicle.makeModel, after: vehicle.makeModel };
            changedFields.push('make/model');
          }
          if (vehicle.year && currentVehicle.year !== vehicle.year) {
            changes['Year'] = { before: currentVehicle.year, after: vehicle.year };
            changedFields.push('year');
          }
          if (vehicle.carStatus && currentVehicle.carStatus !== vehicle.carStatus) {
            changes['Status'] = { before: currentVehicle.carStatus, after: vehicle.carStatus };
            changedFields.push('status');
          }
          if (vehicle.vinNumber && currentVehicle.vinNumber !== vehicle.vinNumber) {
            changes['VIN'] = { before: currentVehicle.vinNumber, after: vehicle.vinNumber };
            changedFields.push('VIN');
          }
          if (vehicle.insuranceExpiry && currentVehicle.insuranceExpiry !== vehicle.insuranceExpiry) {
            changes['Insurance Expiry'] = { before: currentVehicle.insuranceExpiry, after: vehicle.insuranceExpiry };
            changedFields.push('insurance expiry');
          }
          if (vehicle.registrationExpiry && currentVehicle.registrationExpiry !== vehicle.registrationExpiry) {
            changes['Registration Expiry'] = { before: currentVehicle.registrationExpiry, after: vehicle.registrationExpiry };
            changedFields.push('registration expiry');
          }
        }
        
        if (Object.keys(changes).length > 0) {
          const vehicleName = vehicle.licensePlate || currentVehicle?.licensePlate || `Vehicle ID: ${id}`;
          const vehicleInfo = currentVehicle ? `${vehicleName} (${currentVehicle.makeModel})` : vehicleName;
          await logActivity({
            entity_type: 'vehicle',
            entity_id: id,
            action_type: 'update',
            changes,
            description: `Updated vehicle: ${vehicleInfo} (changed: ${changedFields.join(', ')})`,
          });
          console.log('✅ Vehicle update logged to activity log');
        }
      } catch (logError) {
        console.error('⚠️ Failed to log vehicle update:', logError);
        // Don't fail the update if logging fails
      }
      
      // Convert from database schema to app schema  
      const formattedVehicle: Vehicle = {
        id: updatedData.id,
        licensePlate: updatedData.license_plate,
        makeModel: updatedData.make_model,
        year: updatedData.year,
        vinNumber: updatedData.vin,
        image_url: updatedData.image_url,
        carStatus: updatedData.car_status as any,
        currentDriverId: updatedData.current_driver_id,
        dailyRent: updatedData.daily_rental_rate,
        dailyRentalRate: updatedData.daily_rental_rate,
        monthlyRentalRate: updatedData.monthly_rental_rate,
        insuranceExpiry: updatedData.insurance_expiry,
        registrationExpiry: updatedData.registration_expiry,
        notes: updatedData.notes,
        created_at: updatedData.created_at,
      };
      
      console.log('📋 Formatted vehicle for store:', formattedVehicle);
      
      // Update local state immediately with fresh data from database
      set({ 
        vehicles: get().vehicles.map(v => v.id === id ? formattedVehicle : v),
        selectedVehicle: formattedVehicle,
        loading: false 
      });
      
      // Refresh cache in background (non-blocking)
      try {
        await vehicleHybridStore.refreshFromServer();
        console.log('💾 Cache refreshed in background');
      } catch (cacheError) {
        console.warn('⚠️ Cache refresh failed (non-critical):', cacheError);
      }
    } catch (error: any) {
      console.error('❌ Error updating vehicle (hybrid):', error);
      set({ loading: false, error: error.message });
    }
  },
  
  removeVehicle: async (id) => {
    set({ loading: true, error: null });
    try {
      // Delete directly from database (bypass hybrid storage for immediate effect)
      await deleteVehicle(id);
      console.log('✅ Vehicle deleted from database:', id);
      
      // Update local state immediately
      set({ 
        vehicles: get().vehicles.filter(v => v.id !== id),
        selectedVehicle: get().selectedVehicle?.id === id ? null : get().selectedVehicle,
        loading: false 
      });
      
      // Refresh cache in background to stay in sync
      setTimeout(() => {
        vehicleHybridStore.refreshFromServer().catch(err => 
          console.warn('⚠️ Background cache refresh failed:', err)
        );
      }, 100);
    } catch (error: any) {
      console.error('❌ Error deleting vehicle:', error);
      set({ loading: false, error: error.message });
      throw error; // Re-throw so UI can show error
    }
  },
  
  setSelectedVehicle: (vehicle) => {
    set({ selectedVehicle: vehicle });
  },
  
  assignVehicle: async (vehicleId, driverId) => {
    set({ loading: true, error: null });
    try {
      // Mark assignment timestamp to prevent conflicting refreshes
      localStorage.setItem('lastVehicleAssignmentUpdate', Date.now().toString());
      
      // Update vehicle status using hybrid storage (offline-first)
      await vehicleHybridStore.update(vehicleId, { 
        car_status: 'work',
        current_driver_id: driverId,
        assignment_date: new Date().toISOString() // Track assignment date
      });
      
      console.log('✅ Vehicle assigned (hybrid):', { vehicleId, driverId });
      
      // Sync status to ensure consistency (if online)
      try {
        await syncVehicleStatus(vehicleId);
      } catch (syncError) {
        console.warn('⚠️ Vehicle status sync failed (will retry later):', syncError);
      }
      
      // Update local state
      set({ 
        vehicles: get().vehicles.map(v => 
          v.id === vehicleId 
            ? { ...v, carStatus: 'work', currentDriverId: driverId }
            : v
        ),
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error assigning vehicle (hybrid):', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  unassignVehicle: async (vehicleId) => {
    set({ loading: true, error: null });
    try {
      // Mark assignment timestamp to prevent conflicting refreshes
      localStorage.setItem('lastVehicleAssignmentUpdate', Date.now().toString());
      
      // Update vehicle status using hybrid storage (offline-first)
      await vehicleHybridStore.update(vehicleId, { 
        car_status: 'available',
        current_driver_id: null, // Use null instead of undefined
        assignment_date: null // Clear assignment date
      });
      
      console.log('✅ Vehicle unassigned (hybrid):', vehicleId);
      
      // Sync status to ensure consistency (if online)
      try {
        await syncVehicleStatus(vehicleId);
      } catch (syncError) {
        console.warn('⚠️ Vehicle status sync failed (will retry later):', syncError);
      }
      
      // Update local state
      set({ 
        vehicles: get().vehicles.map(v => 
          v.id === vehicleId 
            ? { ...v, carStatus: 'available', currentDriverId: undefined }
            : v
        ),
        loading: false 
      });
    } catch (error: any) {
      console.error('❌ Error unassigning vehicle (hybrid):', error);
      set({ loading: false, error: error.message });
      throw error;
    }
  },

  // Enhanced unassignment that should be used from UI - triggers delivery form
  unassignVehicleWithDelivery: (vehicleId, navigation) => {
    // Navigate to VehicleAssignmentScreen with vehicleId to trigger delivery form workflow
    navigation.navigate('VehicleAssignment', { vehicleId, mode: 'unassign' });
  },
}));

// Listen for vehicle status updates from debug functions
if (typeof window !== 'undefined') {
  window.addEventListener('vehicleStatusUpdated', () => {
    console.log('🔄 Vehicle status updated - refreshing vehicle store...');
    useVehicleStore.getState().fetchVehicles();
  });
}
